import { supabase } from "./supabase";

/**
 * O IO da Correção CFC (000384): o quadro público e a aba da equipe. ZERO regra: quem decide o que o
 * quadro mostra, o CF de cada posição, a situação de cada linha do comparativo e se um ato pode ser
 * feito é o BANCO, e a recusa vem com a frase pronta (`detalhe`). Nenhum `select` cru: as tabelas
 * `corr_*` têm `revoke all`.
 */

export type Resposta = { ok: boolean; motivo?: string; detalhe?: string };

export type QuadroCaderno = { titulo: string; ordem: number; questoes: { numero: number; alternativa: string | null }[] };
export type Quadro =
  | { ok: true; edicao: string; mostrar_download: boolean; cadernos: QuadroCaderno[] }
  | { ok: false; motivo: string };

export type Edicao = { id: string; titulo: string; data_prova: string | null; atual: boolean };
export type Questao = {
  id: string;
  numero: number;
  alternativa: string | null;
  enunciado: string | null;
  texto_alternativa: string | null;
  professor: string | null;
  visivel: boolean;
  respondida: boolean;
  atualizado_em: string;
};
export type Situacao = "confere" | "diverge" | "incompleta" | "sem_cf";
export type Posicao = {
  numero: number;
  questao_id: string | null;
  questao_numero: number | null;
  cf: string | null;
  oficial: string | null;
  papiro: string | null;
  grancursos: string | null;
  oswaldo: string | null;
  estrategia: string | null;
  situacao: Situacao;
};
export type Caderno = { id: string; titulo: string; ordem: number; referencia: boolean; posicoes: Posicao[] };
export type Mudanca = { ato: string; alvo: string | null; motivo: string | null; em: string; por: string | null };
export type Leitura = {
  ok: true;
  pode_editar: boolean;
  edicoes: Edicao[];
  edicao: (Edicao & { mostrar_download: boolean; editada_no_hub_em: string | null }) | null;
  questoes: Questao[];
  cadernos: Caderno[];
  registro: Mudanca[];
};

export const COLUNAS_DOS_CONCORRENTES = [
  { id: "oficial", rotulo: "Oficial" },
  { id: "papiro", rotulo: "Papiro" },
  { id: "grancursos", rotulo: "Gran Cursos" },
  { id: "oswaldo", rotulo: "Oswaldo" },
  { id: "estrategia", rotulo: "Estratégia" },
] as const;
export type ColunaDoConcorrente = (typeof COLUNAS_DOS_CONCORRENTES)[number]["id"];

/** As alternativas que a casa pode marcar, na ordem do seletor. A lista é a da constraint do banco. */
export const ALTERNATIVAS = ["A", "B", "C", "D", "A/R", "B/R", "C/R", "D/R", "ANULADO", "RECURSO"] as const;

/** A frase de uma recusa: a do banco, ou a falha de conexão dita como tal. */
export function frase(r: Resposta | null | undefined): string {
  if (!r) return "Não foi possível falar com o hub. Confira a conexão e tente de novo.";
  return r.detalhe ?? (r.ok ? "Feito." : "Não foi possível fazer isto agora.");
}

async function chamar<T>(nome: string, args?: Record<string, unknown>): Promise<T | Resposta> {
  const { data, error } = await supabase.rpc(nome, args ?? {});
  if (error) return { ok: false, motivo: "falha", detalhe: "Não foi possível falar com o hub. Confira a conexão e tente de novo." };
  return data as T;
}

/** O quadro público. Sem sessão: a função é a única que o anônimo executa. */
export async function lerQuadro(): Promise<Quadro | null> {
  const { data, error } = await supabase.rpc("correcao_quadro");
  if (error) return null;
  return data as Quadro;
}

export const lerCorrecao = (edicao?: string | null) =>
  chamar<Leitura>("corr_admin_ler", { p_edicao: edicao ?? null });
export const corrigir = (questao: string, alternativa: string | null, enunciado: string, texto: string) =>
  chamar<Resposta>("corr_admin_corrigir", { p_questao: questao, p_alternativa: alternativa, p_enunciado: enunciado, p_texto: texto });
export const vincular = (caderno: string, numero: number, questao: string | null) =>
  chamar<Resposta>("corr_admin_vincular", { p_caderno: caderno, p_numero: numero, p_questao: questao });
export const gravarConcorrente = (caderno: string, numero: number, coluna: ColunaDoConcorrente, valor: string) =>
  chamar<Resposta & { valor?: string | null }>("corr_admin_concorrente", { p_caderno: caderno, p_numero: numero, p_coluna: coluna, p_valor: valor });
export const exibir = (edicao: string, questao: string | null, visivel: boolean) =>
  chamar<Resposta & { mudadas?: number }>("corr_admin_exibir", { p_edicao: edicao, p_questao: questao, p_visivel: visivel });
export const configurar = (edicao: string, atual: boolean | null, mostrarDownload: boolean | null) =>
  chamar<Resposta>("corr_admin_configurar", { p_edicao: edicao, p_atual: atual, p_mostrar_download: mostrarDownload });
export const novaEdicao = (titulo: string, dataProva: string | null, cadernos: number, questoes: number, motivo: string) =>
  chamar<Resposta & { id?: string }>("corr_admin_nova_edicao", {
    p_titulo: titulo, p_data_prova: dataProva || null, p_cadernos: cadernos, p_questoes: questoes, p_motivo: motivo,
  });
